import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  SquaresFour, Sparkle, VideoCamera, VideoCameraSlash, UsersThree, FloppyDisk, MapPinArea,
  WarningCircle, ArrowClockwise, Bell, BellSlash, Buildings, PencilSimple,
} from "@phosphor-icons/react";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Stat({ Icon, label, value, color }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-3 flex items-center gap-3" data-testid={`area-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={22} weight="bold" className={color} />
      <div><p className="font-display font-black text-xl leading-none">{value}</p><p className="font-mono-data text-[9px] uppercase tracking-widest text-[#71717A] mt-1">{label}</p></div>
    </div>
  );
}

export default function WorkAreas() {
  const [departments, setDepartments] = useState([]);
  const [names, setNames] = useState([]);
  const [totals, setTotals] = useState({ roaming_staff: 0, total_cameras: 0 });
  const [proposal, setProposal] = useState(null); // [{camera_id,name,zone,department}]
  const [grouping, setGrouping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("");
  const [report, setReport] = useState({ events: [], top_offenders: [], total: 0, enabled: true });
  const [nightCrew, setNightCrew] = useState({ enabled: true, start: "22:00", end: "07:00", store_tz: "" });
  const [savingNightCrew, setSavingNightCrew] = useState(false);

  const load = useCallback(() => {
    api.get("/departments").then((r) => {
      setDepartments(r.data.departments || []);
      setNames(r.data.names || []);
      setTotals({ roaming_staff: r.data.roaming_staff || 0, total_cameras: r.data.total_cameras || 0 });
    }).catch(() => {});
    api.get("/area/report").then((r) => setReport(r.data || {})).catch(() => {});
    api.get("/settings/night-crew").then((r) => setNightCrew(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const autoGroup = async () => {
    setGrouping(true);
    try {
      const r = await api.post("/cameras/auto-group", {});
      setProposal(r.data.assignments || []);
      setSource(r.data.source || "");
      toast.success(r.data.source === "ai" ? "AI grouped your cameras — review & edit below" : "Grouped by name — review & edit below");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Auto-group failed"); }
    finally { setGrouping(false); }
  };
  const editProposal = (cid, dept) => setProposal((p) => p.map((a) => a.camera_id === cid ? { ...a, department: dept } : a));
  const saveProposal = async () => {
    if (!proposal) return;
    const assignments = {};
    proposal.forEach((a) => { assignments[a.camera_id] = (a.department || "").trim(); });
    setSaving(true);
    try {
      const r = await api.post("/cameras/departments", { assignments });
      toast.success(`Saved — ${r.data.updated} camera${r.data.updated === 1 ? "" : "s"} grouped`);
      setProposal(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const toggleWatch = async () => {
    const nv = !report.enabled;
    try { await api.post("/settings/area-watch", { enabled: nv }); setReport((r) => ({ ...r, enabled: nv })); toast.success(nv ? "Out-of-area alerts ON" : "Out-of-area alerts OFF"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update"); }
  };
  const saveNightCrew = async () => {
    setSavingNightCrew(true);
    try {
      const r = await api.put("/settings/night-crew", { enabled: nightCrew.enabled, start: nightCrew.start, end: nightCrew.end });
      setNightCrew((nc) => ({ ...nc, ...r.data }));
      toast.success("Night-crew window saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
    finally { setSavingNightCrew(false); }
  };
  const toggleCamera = async (cam) => {
    const active = cam.disabled; // becoming active if currently disabled
    try {
      await api.post(`/cameras/${cam.id}/active`, { active });
      toast.success(active ? `${cam.name} reactivated` : `${cam.name} deactivated — AI will stop analyzing it`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update camera"); }
  };
  const renameCamera = async (cam) => {
    const name = window.prompt("Rename this camera:", cam.name);
    if (name === null) return;
    const clean = name.trim();
    if (!clean || clean === cam.name) return;
    try {
      await api.patch(`/cameras/${cam.id}`, { name: clean });
      toast.success(`Renamed to ${clean}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not rename"); }
  };

  const allDeptOpts = Array.from(new Set([
    ...names, "Cashier", "Deli", "Meat", "Produce", "Grocery", "Dairy",
    "Bakery", "Seafood", "Entrance", "Office", "Exit", "Admin",
  ]));
  // Group the live proposal by department so the review reads like real areas.
  const grouped = {};
  (proposal || []).forEach((a) => { (grouped[a.department || "Unassigned"] ||= []).push(a); });

  return (
    <div data-testid="work-areas-page" className="space-y-4">
      <div className="bg-[#121214] border border-white/10 p-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <MapPinArea size={18} weight="bold" className="text-[#30D5C8]" /> Work Areas &amp; Departments
        </h2>
        <p className="text-xs text-[#A1A1AA] leading-relaxed mt-1">
          Group your cameras into real departments (Produce, Meat, Cashier…). Assign each employee a home area on the <span className="text-[#BF5AF2]">People &amp; ID</span> page. If a <span className="text-[#32D74B]">Produce</span> employee is recognised in <span className="text-[#FF9F0A]">Meat</span>, a manager gets an alert. Admins &amp; roaming staff are never tracked.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button data-testid="auto-group-btn" onClick={autoGroup} disabled={grouping}
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#30D5C8]/15 text-[#30D5C8] border border-[#30D5C8]/40 hover:bg-[#30D5C8]/25 disabled:opacity-40 transition-colors">
            <Sparkle size={14} weight="fill" className={grouping ? "animate-pulse" : ""} /> {grouping ? "Grouping…" : "Auto-Group Cameras"}
          </button>
          {proposal && (
            <>
              <button data-testid="save-departments-btn" onClick={saveProposal} disabled={saving}
                className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 disabled:opacity-40 transition-colors">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Grouping"}
              </button>
              <button data-testid="cancel-grouping-btn" onClick={() => setProposal(null)}
                className="text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 border border-white/10 text-[#A1A1AA] hover:border-white/30 transition-colors">Cancel</button>
              {source === "ai" && <span className="font-mono-data text-[9px] px-1.5 py-1 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 flex items-center gap-1"><Sparkle size={10} weight="fill" /> AI PROPOSAL</span>}
            </>
          )}
          <button data-testid="area-watch-toggle" onClick={toggleWatch} title="Alert managers when staff leave their work area"
            className={`ml-auto flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 border transition-colors ${report.enabled ? "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40" : "bg-white/5 text-[#71717A] border-white/10"}`}>
            {report.enabled ? <Bell size={14} weight="bold" /> : <BellSlash size={14} weight="bold" />} Out-of-Area Alerts {report.enabled ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat Icon={Buildings} label="Departments" value={departments.length} color="text-[#30D5C8]" />
        <Stat Icon={VideoCamera} label="Cameras" value={totals.total_cameras} color="text-[#0A84FF]" />
        <Stat Icon={UsersThree} label="Roaming Staff" value={totals.roaming_staff} color="text-[#FF9F0A]" />
        <Stat Icon={WarningCircle} label="Out of Area (7d)" value={report.total || 0} color="text-[#FF3B30]" />
      </div>

      {/* Editable auto-group proposal */}
      {proposal && (
        <div className="bg-[#121214] border border-[#30D5C8]/40 p-4 space-y-3" data-testid="proposal-panel">
          <h3 className="font-display font-black uppercase tracking-tight text-xs flex items-center gap-2">
            <SquaresFour size={15} weight="bold" className="text-[#30D5C8]" /> Review Grouping — change any department, then Save
          </h3>
          <div className="space-y-3">
            {Object.entries(grouped).sort((a, b) => b[1].length - a[1].length).map(([dep, cams]) => (
              <div key={dep} className="border border-white/10 p-3" data-testid={`proposal-group-${dep}`}>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#30D5C8] mb-2">{dep} · {cams.length} cam{cams.length === 1 ? "" : "s"}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {cams.map((a) => (
                    <div key={a.camera_id} className="bg-[#1A1A1E] border border-white/10 p-2 flex items-center gap-2">
                      <VideoCamera size={16} weight="bold" className="text-[#71717A] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs truncate">{a.name}</p>
                        <p className="font-mono-data text-[9px] text-[#64748B] truncate">was: {a.zone || "—"}</p>
                      </div>
                      <input list="area-dept-opts" data-testid={`proposal-dept-${a.camera_id}`} value={a.department}
                        onChange={(e) => editProposal(a.camera_id, e.target.value)}
                        className="w-24 bg-[#0F0F12] border border-white/10 text-xs px-1.5 py-1 outline-none focus:border-[#30D5C8]" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <datalist id="area-dept-opts">{allDeptOpts.map((d) => <option key={d} value={d} />)}</datalist>
        </div>
      )}

      {/* Current departments */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="departments-grid">
        {departments.length === 0 && !proposal && (
          <div className="col-span-full border border-white/10 p-6 text-center">
            <p className="font-mono-data text-xs text-[#71717A]">No departments yet. Tap <span className="text-[#30D5C8]">Auto-Group Cameras</span> to organise them.</p>
          </div>
        )}
        {departments.map((d) => (
          <div key={d.department} data-testid={`department-card-${d.department}`} className="bg-[#121214] border border-white/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-display font-black tracking-tight text-sm text-[#30D5C8]">{d.department}</p>
              <span className="font-mono-data text-[10px] text-[#71717A] flex items-center gap-1"><UsersThree size={12} weight="bold" /> {d.employee_count} staff</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {d.cameras.map((c) => (
                <span key={c.id} className={`inline-flex items-center border transition-colors ${c.disabled ? "bg-[#FF3B30]/10 border-[#FF3B30]/40" : "bg-white/5 border-white/10 hover:border-[#32D74B]/50"}`}>
                  <button data-testid={`camera-toggle-${c.id}`} onClick={() => toggleCamera(c)}
                    title={c.disabled ? "Deactivated — click to reactivate" : "Active — click to deactivate (stops AI analysis)"}
                    className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 ${c.disabled ? "text-[#FF3B30] line-through" : "text-[#A1A1AA]"}`}>
                    {c.disabled ? <VideoCameraSlash size={10} weight="bold" /> : <VideoCamera size={10} weight="bold" />} {c.name}
                  </button>
                  <button data-testid={`camera-rename-${c.id}`} onClick={() => renameCamera(c)} title="Rename camera"
                    className="px-1 py-0.5 text-[#64748B] hover:text-[#30D5C8] border-l border-white/10">
                    <PencilSimple size={10} weight="bold" />
                  </button>
                </span>
              ))}
              {d.cameras.length === 0 && <span className="font-mono-data text-[10px] text-[#64748B] italic">No camera assigned yet</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Out-of-area report */}
      <div className="bg-[#121214] border border-white/10 p-4 space-y-3" data-testid="area-report">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black uppercase tracking-tight text-xs flex items-center gap-2">
            <WarningCircle size={15} weight="bold" className="text-[#FF3B30]" /> Staff Out of Their Area (last 7 days)
          </h3>
          <button data-testid="area-report-refresh" onClick={load} className="text-[#71717A] hover:text-white"><ArrowClockwise size={15} weight="bold" /></button>
        </div>
        {report.top_offenders?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {report.top_offenders.map((o) => (
              <span key={o.name} className="inline-flex items-center gap-1.5 text-[11px] font-mono-data px-2 py-1 bg-[#FF9F0A]/10 border border-[#FF9F0A]/40 text-[#FF9F0A]">
                {o.name} <span className="opacity-70">×{o.count}</span>
              </span>
            ))}
          </div>
        )}
        {report.events?.length === 0 ? (
          <p className="font-mono-data text-xs text-[#71717A] py-2">No out-of-area sightings yet. Enrolled staff with a home area will show here if a camera in another department recognises them.</p>
        ) : (
          <div className="space-y-1">
            {report.events.map((e) => (
              <div key={e.id} data-testid={`area-event-${e.id}`} className="flex items-center gap-2 text-xs border-b border-white/5 py-1.5">
                <WarningCircle size={14} weight="bold" className="text-[#FF3B30] shrink-0" />
                <span className="font-medium">{e.name}</span>
                <span className="font-mono-data text-[10px] text-[#64748B]">{e.home_department} → seen in</span>
                <span className="font-mono-data text-[10px] text-[#FF9F0A]">{e.seen_department}</span>
                <span className="font-mono-data text-[10px] text-[#71717A] ml-auto">{e.camera} · {fmt(e.ts)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Night-crew window */}
      <div className="bg-[#121214] border border-white/10 p-4 space-y-3" data-testid="night-crew-settings">
        <h3 className="font-display font-black uppercase tracking-tight text-xs flex items-center gap-2">
          <Bell size={15} weight="bold" className="text-[#30D5C8]" /> Night-Crew Window
        </h3>
        <p className="text-xs text-[#A1A1AA] leading-relaxed">
          While your night crew is on shift (or the store is closed, if no window is set), &ldquo;Staff Out of Area&rdquo; and person-detected alerts are muted. Theft, Vandalism and Watchlist alerts stay on the whole time.
          {nightCrew.store_tz && <> Store timezone is currently <span className="text-[#30D5C8]">{nightCrew.store_tz}</span> (set via the <span className="text-[#30D5C8]">STORE_TZ</span> deployment secret).</>}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-wider">
            <input type="checkbox" data-testid="night-crew-enabled" checked={!!nightCrew.enabled}
              onChange={(e) => setNightCrew((nc) => ({ ...nc, enabled: e.target.checked }))} />
            Enabled
          </label>
          <label className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-wider text-[#A1A1AA]">
            Start
            <input type="time" data-testid="night-crew-start" value={nightCrew.start || "22:00"}
              onChange={(e) => setNightCrew((nc) => ({ ...nc, start: e.target.value }))}
              className="bg-[#0F0F12] border border-white/10 text-xs px-2 py-1 outline-none focus:border-[#30D5C8]" />
          </label>
          <label className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-wider text-[#A1A1AA]">
            End
            <input type="time" data-testid="night-crew-end" value={nightCrew.end || "07:00"}
              onChange={(e) => setNightCrew((nc) => ({ ...nc, end: e.target.value }))}
              className="bg-[#0F0F12] border border-white/10 text-xs px-2 py-1 outline-none focus:border-[#30D5C8]" />
          </label>
          <button data-testid="night-crew-save" onClick={saveNightCrew} disabled={savingNightCrew}
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 disabled:opacity-40 transition-colors">
            <FloppyDisk size={14} weight="bold" /> {savingNightCrew ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <p className="text-center font-display font-bold tracking-[0.3em] uppercase text-sm text-[#0A84FF] pt-2" data-testid="areas-branding">Powered by A V Tech</p>
    </div>
  );
}
